import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import QRCodeModal from './QRCodeModal';
import { validateShortCode } from '../utils/reservedWords';
import { getActiveDomains, getPrimaryDomain } from '../utils/domains';
import { shareOnWhatsApp } from '../utils/qrcode';

const { FiLink2, FiCopy, FiCheck, FiAlertCircle, FiQrCode, FiShare, FiChevronDown } = FiIcons;

const URLShortener = ({ onAddUrl, existingUrls = [] }) => {
  const [url, setUrl] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(getPrimaryDomain());
  const [showDomainDropdown, setShowDomainDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastShortened, setLastShortened] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  const activeDomains = getActiveDomains();

  const generateShortCode = () => {
    let shortCode;
    let attempts = 0;
    const maxAttempts = 10;
    
    do {
      shortCode = Math.random().toString(36).substring(2, 8);
      attempts++;
    } while (
      attempts < maxAttempts && 
      (validateShortCode(shortCode).valid === false || 
       existingUrls.some(u => u.shortCode === shortCode))
    );
    
    return shortCode;
  };

  const isValidUrl = (string) => {
    try {
      const url = new URL(string.startsWith('http') ? string : `https://${string}`);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const handleCustomCodeChange = (value) => {
    const cleanValue = value.replace(/[^a-zA-Z0-9-_]/g, '');
    setCustomCode(cleanValue);
    
    if (cleanValue && error) {
      const validation = validateShortCode(cleanValue);
      if (validation.valid) {
        setError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (!isValidUrl(url)) {
      setError('Please enter a valid URL');
      return;
    }

    if (customCode.trim()) {
      const validation = validateShortCode(customCode.trim());
      if (!validation.valid) {
        setError(validation.message);
        return;
      }

      // Check if custom code already exists
      if (existingUrls.some(u => u.shortCode === customCode.trim())) {
        setError('This short code is already in use');
        return;
      }
    }

    setIsLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      const shortCode = customCode.trim() || generateShortCode();
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      
      const urlData = {
        id: Date.now(),
        originalUrl: fullUrl,
        shortCode,
        shortUrl: `${selectedDomain.domain}/${shortCode}`,
        domain: selectedDomain.domain,
        createdAt: new Date().toISOString(),
        clicks: 0
      };

      onAddUrl(urlData);
      setLastShortened(urlData);
      setUrl('');
      setCustomCode('');
    } catch (err) {
      setError('Failed to shorten URL. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(`https://${text}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard');
    }
  };

  const handleWhatsAppShare = () => {
    if (lastShortened) {
      shareOnWhatsApp(lastShortened.shortUrl, 'Check out this link:');
    }
  };

  return (
    <>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-8 mb-8"
      >
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Shorten Your URL</h2>
          <p className="text-gray-600">Transform long URLs into short, shareable links</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
              Enter URL to shorten
            </label>
            <div className="relative">
              <SafeIcon icon={FiLink2} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/very-long-url"
                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-lg"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Domain
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDomainDropdown(!showDomainDropdown)}
                  className="w-full flex items-center justify-between px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white"
                  disabled={isLoading}
                >
                  <span className="text-gray-700">{selectedDomain.domain}</span>
                  <SafeIcon icon={FiChevronDown} className="text-gray-400" />
                </button>
                
                {showDomainDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10">
                    {activeDomains.map((domain) => (
                      <button
                        key={domain.id}
                        type="button"
                        onClick={() => {
                          setSelectedDomain(domain);
                          setShowDomainDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl transition-colors"
                      >
                        <div className="font-medium text-gray-800">{domain.domain}</div>
                        <div className="text-sm text-gray-500">{domain.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="customCode" className="block text-sm font-medium text-gray-700 mb-2">
                Custom short code (optional)
              </label>
              <input
                type="text"
                id="customCode"
                value={customCode}
                onChange={(e) => handleCustomCodeChange(e.target.value)}
                placeholder="my-link"
                className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                disabled={isLoading}
                maxLength={20}
              />
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg"
            >
              <SafeIcon icon={FiAlertCircle} />
              <span>{error}</span>
            </motion.div>
          )}

          <motion.button
            type="submit"
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Shortening...</span>
              </div>
            ) : (
              'Shorten URL'
            )}
          </motion.button>
        </form>

        {lastShortened && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200"
          >
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Your shortened URL:</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-white p-3 rounded-lg border border-emerald-200">
                  <span className="text-emerald-700 font-mono">https://{lastShortened.shortUrl}</span>
                </div>
                <motion.button
                  onClick={() => copyToClipboard(lastShortened.shortUrl)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white p-3 rounded-lg transition-colors duration-200"
                  title="Copy to clipboard"
                >
                  <SafeIcon icon={copied ? FiCheck : FiCopy} />
                </motion.button>
              </div>

              <div className="flex space-x-3">
                <motion.button
                  onClick={() => setShowQRModal(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 bg-white hover:bg-gray-50 text-emerald-700 border border-emerald-200 py-3 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiQrCode} />
                  <span>QR Code</span>
                </motion.button>

                <motion.button
                  onClick={handleWhatsAppShare}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiShare} />
                  <span>WhatsApp</span>
                </motion.button>
              </div>
            </div>

            {copied && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-emerald-600 text-sm mt-2"
              >
                Copied to clipboard!
              </motion.p>
            )}
          </motion.div>
        )}
      </motion.div>

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={lastShortened?.shortUrl}
        title="QR Code for Short URL"
      />
    </>
  );
};

export default URLShortener;